import { darken } from '@mui/system';
import isEqual from 'lodash/isEqual';
import pick from 'lodash/pick';
import { Layout, ResponsiveProps as ResponsiveGridLayoutProps } from 'react-grid-layout';

import type { ChartColor, VisualizationItem, MetricDatum, AggregateByOption } from '../types';

// Shared chart constants
export const X_AXIS_LABEL_ANGLE_ROTATED = 45;
export const X_AXIS_LABEL_ANGLE_STRAIGHT = -Math.PI / 4; // -45 degrees (radians) - standard for readability
export const MINIMUM_TICK_WIDTH_STRAIGHT = 40; // Minimum width for tick labels
export const CHAR_WIDTH_PX = 7.5; // Approximate character width in pixels
export const MINIMUM_TICK_WIDTH_ROTATED = 12; // Minimum width for tick labels

/**
 * Returns a set of colors from the theme's color palette to be used for charts.
 *
 * @param theme The theme object from useTheme hook
 * @returns A list of color setting from the theme
 */
export const getChartColors = (theme: any): ChartColor[] => {
  return [
    {
      default: theme.palette.primary[400], //  #007EA1
      hover: theme.palette.primary[600], //  #08607A
    },
    {
      default: theme.palette.secondary[500], // #A06EFF
      hover: darken(theme.palette.secondary[600], 0.25), // #8349F0
    },
    {
      default: theme.palette.tertiary[500], // #BDE960
      hover: darken(theme.palette.tertiary[600], 0.25), // #A7D841
    },
    {
      default: theme.palette.quaternary[500], // #FF894B
      hover: darken(theme.palette.quaternary[600], 0.25), // #E05E19
    },
  ];
};

export const buildLayouts = (visualizations: VisualizationItem[]): ResponsiveGridLayoutProps['layouts'] => {
  const lgLayout: Layout[] = visualizations.map((viz, index) => {
    return {
      i: viz.id,
      x: viz.x ?? (index % 2) * 6, // Default: 2 columns
      y: viz.y ?? Math.floor(index / 2) * 4, // Default: 4 rows high
      w: viz.w,
      h: viz.h,
    };
  });

  // Generate responsive layouts
  const layouts: ResponsiveGridLayoutProps['layouts'] = { lg: lgLayout };

  // For smaller screens, make everything full width and adjust height
  layouts.md = lgLayout.map((item) => ({ ...item, w: 10, x: 0 }));
  layouts.sm = lgLayout.map((item) => ({ ...item, w: 6, x: 0 }));
  layouts.xs = lgLayout.map((item) => ({ ...item, w: 4, x: 0 }));
  layouts.xxs = lgLayout.map((item) => ({ ...item, w: 2, x: 0 }));

  return layouts;
};

export const compareLgLayouts = (layoutA: Layout[] | undefined, layoutB: Layout[] | undefined): boolean => {
  if (!layoutA && !layoutB) return true;
  if (!layoutA || !layoutB) return false;
  if (layoutA.length !== layoutB.length) return false;

  const normalizeLayout = (layout: Layout[]) =>
    layout.map((item) => pick(item, ['i', 'x', 'y', 'w', 'h'])).sort((a, b) => a.i.localeCompare(b.i));

  const normalizedA = normalizeLayout(layoutA);
  const normalizedB = normalizeLayout(layoutB);

  return isEqual(normalizedA, normalizedB);
};

export const findColumnKey = (dataKeys: string[], field: any): string | null => {
  if (field.field_config?.data_key) {
    // First try exact match (for fields without aggregation)
    const exactMatch = dataKeys.find((key) => key === field.field_config.data_key);
    if (exactMatch) {
      return exactMatch;
    }

    // For fields with aggregation, look for keys that start with the base data_key
    // followed by an underscore and aggregation method (e.g., "_sum", "_mean")
    if (field.aggregation) {
      const aggregationSuffix = `_${field.aggregation.toLowerCase()}`;
      const aggregatedMatch = dataKeys.find((key) => key === field.field_config.data_key + aggregationSuffix);
      if (aggregatedMatch) {
        return aggregatedMatch;
      }
    }
  }

  return null;
};

export const displayLegend = (yAxisKeys: string[]): boolean => {
  return yAxisKeys.length > 1;
};

// Minimum length for an auto-derived band name. Anything shorter falls back to "Range" /
// "Range N" since a too-short prefix would either be empty (e.g. "min"/"max" share nothing)
// or land mid-word ("Forecast m" for "Forecast min"/"Forecast max" if not whitespace-aligned).
const MIN_DERIVED_BAND_NAME_LENGTH = 3;

/**
 * Derive a short, human-readable band name from the two bound-column labels by finding their
 * shared whitespace-aligned prefix and trimming trailing punctuation. Falls back to "Range" or
 * "Range N" when the labels don't share a meaningful prefix (or aren't available).
 *
 * Word-aligned split is intentional: a character-level common prefix could end mid-word and
 * produce names like "Forecast m" for "Forecast min" / "Forecast max".
 */
export const deriveBandName = (
  lowerLabel: string | undefined,
  upperLabel: string | undefined,
  bandIndex: number,
  bandCount: number,
): string => {
  const fallback = bandCount > 1 ? `Range ${bandIndex + 1}` : 'Range';
  if (!lowerLabel || !upperLabel || lowerLabel === upperLabel) return fallback;

  // Split keeps whitespace runs in the array so rejoin reproduces the original spacing exactly.
  const splitOnWhitespace = (s: string): string[] => s.split(/(\s+)/);
  const lowerParts = splitOnWhitespace(lowerLabel);
  const upperParts = splitOnWhitespace(upperLabel);

  let i = 0;
  while (i < lowerParts.length && i < upperParts.length && lowerParts[i] === upperParts[i]) {
    i += 1;
  }

  const sharedPrefix = lowerParts
    .slice(0, i)
    .join('')
    .replace(/[\s\-—–:,_]+$/, '');
  return sharedPrefix.length >= MIN_DERIVED_BAND_NAME_LENGTH ? sharedPrefix : fallback;
};

export const formatLabel = (value: any, showFullLabels = false) => {
  const label = String(value);
  if (!showFullLabels && label.length > 16) {
    return `${label.slice(0, 7)}...${label.substring(label.length - 6)}`;
  }

  return label;
};

/**
 * Number formatter for chart values with currency and comma formatting support
 */
export const formatNumber = ({
  value,
  format,
  maxDecimals,
  aggregationOptions,
}: {
  value: string | number;
  format?: any;
  maxDecimals?: number;
  aggregationOptions?: AggregateByOption[];
}): string => {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  const isAggregationMethodCount = aggregationOptions?.some(
    (agg) => agg.method === 'count' || agg.method === 'nunique',
  );
  if (Number.isNaN(num)) return String(value);

  // Currency formatting
  if (format?.type === 'currency' && !isAggregationMethodCount) {
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: format.currency_code,
        // Force minimum fraction digits if places is specified otherwise default to 0
        minimumFractionDigits: format?.places ? parseInt(format.places, 10) : 0,
        maximumFractionDigits: format?.places ? parseInt(format.places, 10) : 2,
      }).format(num);
    } catch (e) {
      console.error(`formatNumber: invalid currency code "${format.currency_code}"`, e);
      return String(num);
    }
  }

  // Percentage formatting
  if (format?.type === 'percentage' && !isAggregationMethodCount) {
    return new Intl.NumberFormat('en-US', {
      style: 'percent',
      minimumFractionDigits: 0,
      maximumFractionDigits: maxDecimals || 2,
    }).format(num);
  }

  // Number formatting with thousands separators and decimal places
  if (format?.comma) {
    const decimalPlaces = format?.places ? parseInt(format.places, 10) : maxDecimals || 0;
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: decimalPlaces,
      maximumFractionDigits: decimalPlaces,
    }).format(num);
  }

  // Apply decimal limit if specified
  if (maxDecimals !== undefined) {
    const formatted = num.toFixed(maxDecimals);
    // Add commas for large numbers (>= 1000) even when maxDecimals is specified
    if (Math.abs(num) >= 1000) {
      return new Intl.NumberFormat('en-US').format(parseFloat(formatted));
    }
    return formatted;
  }

  // For large numbers (>= 1000), add commas by default
  if (Math.abs(num) >= 1000) {
    return new Intl.NumberFormat('en-US').format(num);
  }

  return String(num);
};

/**
 * Calculate the width needed for Y-axis labels based on the longest formatted value
 */
export const calculateYAxisWidth = (
  data: MetricDatum[],
  yAxisKeys: string[],
  formatValue: (value: any) => string,
  charWidthPx: number = CHAR_WIDTH_PX,
  minimumTickWidth: number = MINIMUM_TICK_WIDTH_STRAIGHT,
): number => {
  const longestYAxisLabelSize = Math.max(
    ...data.reduce(
      (result, datum) => [
        ...result,
        ...yAxisKeys.map((yAxisKey) =>
          // Round to avoid floating points. Its okay to be rough here since its just for spacing.
          // e.g. 10.00000001 as dollars should be length ~3 ($10) instead of  length ~11
          datum[yAxisKey] || datum[yAxisKey] === 0 ? formatValue(Math.round(datum[yAxisKey] as number)).length : 0,
        ),
      ],
      [] as number[],
    ),
  );
  return minimumTickWidth + charWidthPx * longestYAxisLabelSize;
};

/**
 * Calculate spacing needed for X-axis labels based on label length and rotation
 */
export const calculateXAxisLabelSpacing = (
  data: MetricDatum[],
  key: string | number,
  charWidthPx: number = CHAR_WIDTH_PX,
  labelAngle: number = X_AXIS_LABEL_ANGLE_STRAIGHT,
  showFullLabels = false,
): number => {
  const labelLengths = data.map((datum) => String(datum[key]).length);

  const maxLabelLength = Math.max(...labelLengths);

  // We limit this to 16 chars not to break the design
  const originalXAxisLabelSpacing = Math.ceil(Math.min(16, maxLabelLength) * charWidthPx * Math.cos(labelAngle));

  if (!showFullLabels || maxLabelLength <= 16) {
    return originalXAxisLabelSpacing;
  }

  return Math.ceil(maxLabelLength * charWidthPx * Math.cos((labelAngle * Math.PI) / 180));
};

/**
 * Extracts the field name from a data key in the format {Source!FieldName} or {Source!FieldName}_aggregation
 *
 * @param dataKey The data key to extract the field name from (e.g., "{Training!industry}", "{Training!arr}_sum")
 * @returns The extracted field name (e.g., "industry", "arr")
 */
export const extractFieldName = (dataKey: string): string => {
  const match = dataKey.match(/\{.*?!(.+?)(?:_\w+)?\}/);
  return match ? match[1] : dataKey;
};

/**
 * Formats a field name with aggregation for display
 *
 * @param dataKey The data key to format (e.g., "{Training!arr}_sum")
 * @param aggregation Optional aggregation type (e.g., "sum", "mean"). If not provided, will be extracted from dataKey
 * @returns The formatted display name (e.g., "arr (Sum)")
 */
export const formatFieldNameWithAggregation = (dataKey: string, aggregation?: string): string => {
  const fieldName = extractFieldName(dataKey);

  // If aggregation is provided, use it directly
  if (aggregation) {
    const aggregationDisplay =
      aggregation.toLowerCase() !== 'none'
        ? aggregation.charAt(0).toUpperCase() + aggregation.slice(1).toLowerCase()
        : '';
    return aggregationDisplay ? `${fieldName} (${aggregationDisplay})` : fieldName;
  }

  // Otherwise, try to extract aggregation from the dataKey
  const match = dataKey.match(/_(\w+)$/);
  if (match) {
    const extractedAggregation = match[1];
    const aggregationDisplay =
      extractedAggregation.charAt(0).toUpperCase() + extractedAggregation.slice(1).toLowerCase();
    return `${fieldName} (${aggregationDisplay})`;
  }

  return fieldName;
};

/**
 * Date formatting options for axis labels
 * - 'short': e.g., "1/15/24"
 * - 'medium': e.g., "Jan 15, 2024"
 * - 'long': e.g., "January 15, 2024"
 * - 'monthDay': e.g., "Jan 15"
 * - 'monthYear': e.g., "Jan 2024"
 * - 'year': e.g., "2024"
 */
export type DateFormatOption = 'short' | 'medium' | 'long' | 'monthDay' | 'monthYear' | 'year';

/**
 * Detects if a value is an ISO 8601 date string (YYYY-MM-DD)
 */
export const isISODateString = (value: any): boolean => {
  if (typeof value !== 'string') return false;

  // Match ISO 8601 date format: YYYY-MM-DD
  const isoDateRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (!isoDateRegex.test(value)) return false;

  // Verify it's a valid date
  const date = new Date(value);
  return !Number.isNaN(date.getTime());
};

/**
 * Formats an ISO date string according to the specified format
 * Falls back to the original value if formatting fails
 */
export const formatDateString = (value: any, format: DateFormatOption = 'short'): string => {
  if (!isISODateString(value)) {
    return String(value);
  }

  try {
    const date = new Date(value);

    switch (format) {
      case 'short':
        // e.g., "1/15/24"
        return date.toLocaleDateString('en-US', {
          month: 'numeric',
          day: 'numeric',
          year: '2-digit',
        });

      case 'medium':
        // e.g., "Jan 15, 2024"
        return date.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        });

      case 'long':
        // e.g., "January 15, 2024"
        return date.toLocaleDateString('en-US', {
          month: 'long',
          day: 'numeric',
          year: 'numeric',
        });

      case 'monthDay':
        // e.g., "Jan 15"
        return date.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
        });

      case 'monthYear':
        // e.g., "Jan 2024"
        return date.toLocaleDateString('en-US', {
          month: 'short',
          year: 'numeric',
        });

      case 'year':
        // e.g., "2024"
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
        });

      default:
        return String(value);
    }
  } catch (error) {
    // If formatting fails, return original value
    return String(value);
  }
};
